import { useMemo } from "react";
import { useT } from "../i18n.js";

/**
 * EN 15804+A2 / ISO 14040 vocabulary for the LCA editor.
 *
 * The API stores indicator codes, units and modules as free text — an org may
 * declare something no list covers — so this is a *catalog*, not an enum: it
 * prefills the standard unit, gives every code a human name, and keeps the
 * codes the public passport renders with a friendly label (GWP_total, PENRT,
 * WDP — see `passport-html.ts` INDICATOR_LABELS) spelled the same way in
 * every passport instead of once per typist.
 */

export type LcaIndicatorGroup = "impact" | "resource" | "waste";

export const LCA_INDICATORS = [
  /* Core environmental impact indicators (EN 15804+A2, 7.2.3). */
  { code: "GWP_total", unit: "kg CO2 eq", group: "impact" },
  { code: "GWP_fossil", unit: "kg CO2 eq", group: "impact" },
  { code: "GWP_biogenic", unit: "kg CO2 eq", group: "impact" },
  { code: "GWP_luluc", unit: "kg CO2 eq", group: "impact" },
  { code: "ODP", unit: "kg CFC11 eq", group: "impact" },
  { code: "AP", unit: "mol H+ eq", group: "impact" },
  { code: "EP_freshwater", unit: "kg P eq", group: "impact" },
  { code: "EP_marine", unit: "kg N eq", group: "impact" },
  { code: "EP_terrestrial", unit: "mol N eq", group: "impact" },
  { code: "POCP", unit: "kg NMVOC eq", group: "impact" },
  { code: "ADPE", unit: "kg Sb eq", group: "impact" },
  { code: "ADPF", unit: "MJ", group: "impact" },
  { code: "WDP", unit: "m3 world eq", group: "impact" },

  /* Resource use (EN 15804+A2, 7.2.4). */
  { code: "PERE", unit: "MJ", group: "resource" },
  { code: "PERM", unit: "MJ", group: "resource" },
  { code: "PERT", unit: "MJ", group: "resource" },
  { code: "PENRE", unit: "MJ", group: "resource" },
  { code: "PENRM", unit: "MJ", group: "resource" },
  { code: "PENRT", unit: "MJ", group: "resource" },
  { code: "SM", unit: "kg", group: "resource" },
  { code: "RSF", unit: "MJ", group: "resource" },
  { code: "NRSF", unit: "MJ", group: "resource" },
  { code: "FW", unit: "m3", group: "resource" },

  /* Waste categories and output flows (EN 15804+A2, 7.2.5 / 7.2.6). */
  { code: "HWD", unit: "kg", group: "waste" },
  { code: "NHWD", unit: "kg", group: "waste" },
  { code: "RWD", unit: "kg", group: "waste" },
  { code: "CRU", unit: "kg", group: "waste" },
  { code: "MFR", unit: "kg", group: "waste" },
  { code: "MER", unit: "kg", group: "waste" },
  { code: "EEE", unit: "MJ", group: "waste" },
  { code: "EET", unit: "MJ", group: "waste" },
] as const satisfies readonly { code: string; unit: string; group: LcaIndicatorGroup }[];

export type LcaIndicatorDef = (typeof LCA_INDICATORS)[number];
export type LcaIndicatorCode = LcaIndicatorDef["code"];

export const LCA_INDICATOR_GROUPS: LcaIndicatorGroup[] = ["impact", "resource", "waste"];

const BY_CODE = new Map<string, LcaIndicatorDef>(LCA_INDICATORS.map((i) => [i.code, i]));

/** The catalog entry for a code, or `undefined` for an org-defined one. */
export function lcaIndicatorDef(code: string): LcaIndicatorDef | undefined {
  return BY_CODE.get(code.trim());
}

const SUB_SUP: Record<string, string> = {
  "₀": "0",
  "₁": "1",
  "₂": "2",
  "₃": "3",
  "⁰": "0",
  "¹": "1",
  "²": "2",
  "³": "3",
};

/**
 * The leading symbol of a unit, normalized: "kg CO₂e", "kg CO2 eq" and "kgCO2e"
 * all reduce to "kg". Notation for the equivalence part is a matter of taste and
 * flagging it would cry wolf on every row; declaring GWP in "MJ" is a real
 * mistake, and only the stem catches that.
 */
export function unitStem(unit: string): string {
  const head = unit.trim().split(/\s+/)[0] ?? "";
  return head
    .toLowerCase()
    .replace(/[₀₁₂₃⁰¹²³]/g, (c) => SUB_SUP[c] ?? c)
    .replace(/[^a-z0-9]/g, "");
}

/** True when the declared unit contradicts the catalog's for a known code. */
export function unitLooksWrong(code: string, unit: string): boolean {
  const def = lcaIndicatorDef(code);
  if (!def || unit.trim() === "") return false;
  return unitStem(unit) !== unitStem(def.unit);
}

/** Localized name for a known code; the raw code for org-defined ones. */
export function useIndicatorLabel(): (code: string) => string {
  const t = useT();
  return useMemo(
    () => (code: string) => {
      const def = lcaIndicatorDef(code);
      return def ? t(`lcaInd.${def.code}`) : code;
    },
    [t],
  );
}

/** The 13 core impact indicators — the "declare a full EN 15804 profile" preset. */
export const LCA_CORE_SET: LcaIndicatorCode[] = LCA_INDICATORS.filter((i) => i.group === "impact").map(
  (i) => i.code,
);

/** Carbon / energy / water — what a manual declaration realistically ships with. */
export const LCA_HEADLINE_SET: LcaIndicatorCode[] = ["GWP_total", "PENRT", "WDP"];

/**
 * Headline metrics for the summary tiles, in preference order: the first code
 * present in the draft wins, so an EPD that only carries GWP_fossil still gets
 * a carbon tile.
 */
export const LCA_HEADLINES = [
  { key: "carbon", codes: ["GWP_total", "GWP_fossil", "GWP", "CFP"] },
  { key: "energy", codes: ["PENRT", "PERT", "ADPF"] },
  { key: "water", codes: ["WDP", "FW"] },
] as const satisfies readonly { key: string; codes: readonly string[] }[];

export type LcaHeadlineKey = (typeof LCA_HEADLINES)[number]["key"];

/* --- Life-cycle modules (EN 15804 information modules) ------------------------ */

export type LcaModuleGroup = "product" | "construction" | "use" | "endOfLife" | "beyond";

export const LCA_MODULES = [
  { code: "A1", group: "product" },
  { code: "A2", group: "product" },
  { code: "A3", group: "product" },
  { code: "A1-A3", group: "product" },
  { code: "A4", group: "construction" },
  { code: "A5", group: "construction" },
  { code: "B1", group: "use" },
  { code: "B2", group: "use" },
  { code: "B3", group: "use" },
  { code: "B4", group: "use" },
  { code: "B5", group: "use" },
  { code: "B6", group: "use" },
  { code: "B7", group: "use" },
  { code: "C1", group: "endOfLife" },
  { code: "C2", group: "endOfLife" },
  { code: "C3", group: "endOfLife" },
  { code: "C4", group: "endOfLife" },
  { code: "C1-C4", group: "endOfLife" },
  { code: "D", group: "beyond" },
] as const satisfies readonly { code: string; group: LcaModuleGroup }[];

export type LcaModuleCode = (typeof LCA_MODULES)[number]["code"];

export const LCA_MODULE_GROUPS: LcaModuleGroup[] = ["product", "construction", "use", "endOfLife", "beyond"];

const MODULE_CODES = new Set<string>(LCA_MODULES.map((m) => m.code));

export function isKnownModule(code: string): code is LcaModuleCode {
  return MODULE_CODES.has(code);
}

/* --- Methodology presets ------------------------------------------------------ */

/** System boundaries, localized through `lcaBoundary.*`; the code is stored. */
export const LCA_BOUNDARIES = [
  "cradle_to_gate",
  "cradle_to_gate_with_options",
  "cradle_to_grave",
  "cradle_to_cradle",
  "gate_to_gate",
] as const;
export type LcaBoundary = (typeof LCA_BOUNDARIES)[number];

const BOUNDARY_CODES = new Set<string>(LCA_BOUNDARIES);
export function isKnownBoundary(code: string): code is LcaBoundary {
  return BOUNDARY_CODES.has(code);
}

/** Standards a declaration is calculated against — stored verbatim. */
export const LCA_METHODS = [
  "EN 15804+A2",
  "EN 15804+A1",
  "ISO 14040/14044",
  "ISO 14067",
  "EF 3.1 (PEF)",
  "GHG Protocol Product Standard",
  "PAS 2050",
  "Higg MSI",
];

/** Background databases — stored verbatim, versions included on purpose. */
export const LCA_DATASETS = [
  "ecoinvent 3.11",
  "ecoinvent 3.10",
  "EF 3.1",
  "Sphera MLC (GaBi)",
  "Idemat 2024",
  "Agri-footprint 6",
  "World Food LCA Database",
  "Higg MSI 3.7",
];

/** Functional-unit presets; the *localized* string is what gets stored. */
export const LCA_FUNCTIONAL_UNITS = ["piece", "kg", "m2", "m", "l", "pair", "k1000"] as const;
export type LcaFunctionalUnitKey = (typeof LCA_FUNCTIONAL_UNITS)[number];

/** Units offered by the unit picker: every catalog unit first, then the usual extras. */
export const LCA_UNITS: string[] = (() => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const unit of [
    ...LCA_INDICATORS.map((i) => i.unit),
    "kg CO2e",
    "t CO2 eq",
    "g CO2 eq",
    "kWh",
    "L",
    "m2",
    "g",
    "pcs",
  ]) {
    if (seen.has(unit)) continue;
    seen.add(unit);
    out.push(unit);
  }
  return out;
})();
